import { z } from 'zod';
import type { FieldConfig } from '@/components/blocks/forms';
import type {
  AssertFieldKeysMatch,
  AssertSchemaKeyCoverage,
  SchemaFieldKeys,
} from '@/components/blocks/forms/schemaSync';
import { optionalText, requiredText } from '@/lib/formSchemas';
import { companyFieldTooltips } from './masterDataFieldTooltips';

export const companyFormSchema = z.object({
  name: requiredText(2, 120, {
    min: 'Inserisci la ragione sociale completa',
    max: 'Massimo 120 caratteri',
  }),
  code: requiredText(1, 40, {
    min: 'Inserisci il codice azienda',
    max: 'Massimo 40 caratteri',
  }),
  vat: requiredText(5, 20, {
    min: 'Inserisci la partita IVA',
    max: 'Massimo 20 caratteri',
  }),
  sedeLegaleAddress: requiredText(4, 160, {
    min: "Inserisci l'indirizzo",
    max: 'Massimo 160 caratteri',
  }),
  sedeLegaleCap: requiredText(4, 10, {
    min: 'Inserisci il CAP',
    max: 'Massimo 10 caratteri',
  }),
  sedeLegaleCity: requiredText(2, 80, {
    min: 'Inserisci il comune',
    max: 'Massimo 80 caratteri',
  }),
  sedeLegaleProvince: requiredText(2, 40, {
    min: 'Inserisci la provincia',
    max: 'Massimo 40 caratteri',
  }),
  logoUrl: z.string().optional(),
  notes: optionalText(320),
});

export const companyFormFieldKeys = [
  'name',
  'code',
  'vat',
  'sedeLegaleAddress',
  'sedeLegaleCap',
  'sedeLegaleCity',
  'sedeLegaleProvince',
  'notes',
] as const satisfies SchemaFieldKeys<typeof companyFormSchema>;

export const companyFormExtraSchemaKeys = ['logoUrl'] as const satisfies SchemaFieldKeys<
  typeof companyFormSchema
>;

const _companyFormSchemaCoverageCheck: AssertSchemaKeyCoverage<
  typeof companyFormSchema,
  typeof companyFormFieldKeys,
  typeof companyFormExtraSchemaKeys
> = true;
void _companyFormSchemaCoverageCheck;

/**
 * SINGLE SOURCE OF TRUTH for section configuration.
 * Both forms and anagrafica derive their sections from this.
 * Order determines rendering sequence everywhere.
 */
export const companySections = {
  general: { title: 'Informazioni generali', order: 1 },
  notes: { title: 'Note', order: 2 }, // Form-only (notes field has displayFormatter: "skip")
} as const;

// Derive form section order from companySections
const companyFormSectionOrder = Object.entries(companySections)
  .sort(([, a], [, b]) => a.order - b.order)
  .map(([key]) => key);

// Derive form section titles from companySections
const companyFormSectionTitles = Object.fromEntries(
  Object.entries(companySections).map(([key, { title }]) => [key, title])
) as Record<string, string>;

// Generate form sections from field definitions
function generateFormSections(
  fields: readonly { readonly key: string; readonly section?: string }[],
  sectionOrder: string[],
  sectionTitles: Record<string, string>
) {
  const groups = new Map<string, string[]>();

  // Group fields by section
  for (const field of fields) {
    const section = field.section ?? 'general';
    const group = groups.get(section) ?? [];
    group.push(field.key);
    groups.set(section, group);
  }

  // Build sections in order
  return sectionOrder
    .filter((key) => groups.has(key))
    .map((key) => ({
      title: sectionTitles[key] ?? key,
      fields: groups.get(key)!,
    }));
}

export const companyFormFields = [
  {
    key: 'name',
    label: 'Ragione sociale',
    type: 'text',
    required: true,
    placeholder: 'Es: FrigoLinea S.p.A.',
    section: 'general',
  },
  {
    key: 'code',
    label: 'Codice',
    type: 'text',
    required: true,
    placeholder: 'Es: FRLN',
    tooltip: companyFieldTooltips.code,
    section: 'general',
  },
  {
    key: 'vat',
    label: 'Partita IVA',
    type: 'text',
    required: true,
    placeholder: 'Es: IT01234567890',
    section: 'general',
  },
  {
    key: 'sedeLegaleAddress',
    label: 'Indirizzo',
    type: 'text',
    required: true,
    placeholder: 'Es: Via Industriale 12',
    tooltip: companyFieldTooltips.address,
    colSpan: 2,
    section: 'general',
  },
  {
    key: 'sedeLegaleCap',
    label: 'CAP',
    type: 'text',
    required: true,
    placeholder: 'Es: 40100',
    colSpan: 2,
    section: 'general',
  },
  {
    key: 'sedeLegaleCity',
    label: 'Comune',
    type: 'text',
    required: true,
    placeholder: 'Es: Modena',
    section: 'general',
  },
  {
    key: 'sedeLegaleProvince',
    label: 'Provincia',
    type: 'text',
    required: true,
    placeholder: 'Es: MO',
    section: 'general',
  },
  {
    key: 'notes',
    label: 'Note',
    type: 'textarea',
    placeholder: 'Note facoltative',
    tooltip: companyFieldTooltips.notes,
    section: 'notes',
    displayFormatter: 'skip', // Manual section with stacked layout
  },
] as const satisfies readonly FieldConfig[];

const _companyFormFieldKeyMatch: AssertFieldKeysMatch<
  typeof companyFormFieldKeys,
  typeof companyFormFields
> = companyFormFields;
void _companyFormFieldKeyMatch;

/**
 * Form sections generated from field definitions.
 * Single source of truth - derived from section property on each field.
 */
export const companyFormSections = generateFormSections(
  companyFormFields,
  companyFormSectionOrder,
  companyFormSectionTitles
);

/**
 * Flat field order derived from form sections.
 * Use this for useUnifiedForm's fieldOrder parameter.
 */
export const companyFieldOrder = companyFormSections.flatMap((s) => s.fields);

export type CompanyFormValues = z.output<typeof companyFormSchema>;
